/**
 * ObstaclePropertiesPanel — Edit properties for a selected obstacle
 * Shown when an obstacle is selected in the ObstaclePanel list or side panel.
 * Uses explicit Save button pattern (no auto-save on blur).
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useObstacleMutations, obstacleKeys } from '@/hooks/useObstacles'
import {
  OBSTACLE_TYPE_LABELS,
  OBSTACLE_MOBILITY_LABELS,
  type Obstacle,
  type ObstacleType,
  type ObstacleMobility,
  type ObstacleShape,
  type UpdateObstacleInput,
} from '@/types/obstacle'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Slider } from '@/components/ui/slider'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ArrowLeft, X, Copy, Trash2, RotateCcw, Save } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface ObstaclePropertiesPanelProps {
  obstacle: Obstacle
  onBack: () => void
  /** Header icon: 'back' shows ArrowLeft, 'close' shows X */
  closeIcon?: 'back' | 'close'
}

const OBSTACLE_TYPES: ObstacleType[] = [
  'column', 'wall', 'firewall', 'office', 'storage',
  'restroom', 'equipment', 'vehicle', 'staging',
  'barrier', 'utility', 'other',
]

const SHAPES: ObstacleShape[] = ['rectangle', 'circle']
const MOBILITIES: ObstacleMobility[] = ['static', 'semi_permanent', 'dynamic']

export function ObstaclePropertiesPanel({ obstacle, onBack, closeIcon = 'back' }: ObstaclePropertiesPanelProps) {
  const { update, duplicate, remove } = useObstacleMutations()
  const queryClient = useQueryClient()

  // Snapshot of last-saved server state — dirty tracking compares against this
  const savedRef = useRef<Obstacle>(obstacle)

  // Push overrides to all obstacle list queries in cache (live 3D preview, no DB round-trip)
  const pushToCache = useCallback((patch: Partial<Obstacle>) => {
    const id = obstacle.id
    const queries = queryClient.getQueriesData<Obstacle[]>({ queryKey: obstacleKeys.lists() })
    for (const [key] of queries) {
      queryClient.setQueryData<Obstacle[]>(key, (old) =>
        old?.map((o) => (o.id === id ? { ...o, ...patch } : o))
      )
    }
  }, [queryClient, obstacle.id])

  // Local form state — edited freely, persisted only on Save
  const [name, setName] = useState(obstacle.name)
  const [obstacleType, setObstacleType] = useState(obstacle.obstacleType)
  const [mobility, setMobility] = useState(obstacle.mobility)
  const [shape, setShape] = useState(obstacle.shape)
  const [width, setWidth] = useState(obstacle.width?.toString() ?? '')
  const [depth, setDepth] = useState(obstacle.depth?.toString() ?? '')
  const [radius, setRadius] = useState(obstacle.radius?.toString() ?? '')
  const [height, setHeight] = useState(obstacle.height.toString())
  const [elevation, setElevation] = useState(obstacle.elevation.toString())
  const [posX, setPosX] = useState(obstacle.positionX.toString())
  const [posY, setPosY] = useState(obstacle.positionY.toString())
  const [rotation, setRotation] = useState(obstacle.rotation.toString())
  const [color, setColor] = useState(obstacle.color)
  const [opacity, setOpacity] = useState(obstacle.opacity)
  const [labelVisible, setLabelVisible] = useState(obstacle.labelVisible)
  const [participatesInCollision, setParticipatesInCollision] = useState(obstacle.participatesInCollision)
  const [clearanceBuffer, setClearanceBuffer] = useState(obstacle.clearanceBufferFt.toString())
  const [isActive, setIsActive] = useState(obstacle.isActive)
  const [notes, setNotes] = useState(obstacle.notes ?? '')

  // Full reset when switching to a different obstacle
  useEffect(() => {
    savedRef.current = obstacle
    resetForm()
  }, [obstacle.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Restore saved state to cache on unmount (discard unsaved drag/visual changes)
  useEffect(() => {
    return () => {
      const saved = savedRef.current
      const queries = queryClient.getQueriesData<Obstacle[]>({ queryKey: obstacleKeys.lists() })
      for (const [key] of queries) {
        queryClient.setQueryData<Obstacle[]>(key, (old) =>
          old?.map((o) => (o.id === saved.id ? saved : o))
        )
      }
    }
  }, [queryClient])  

  // Sync position/rotation from external changes (drag, keyboard rotation)
  useEffect(() => {
    setPosX(obstacle.positionX.toString())
    setPosY(obstacle.positionY.toString())
    setRotation(obstacle.rotation.toString())
  }, [obstacle.positionX, obstacle.positionY, obstacle.rotation])

  // Live preview — push visual fields to cache so 3D model reflects form edits in real-time
  useEffect(() => {
    pushToCache({
      shape,
      width: shape === 'rectangle' ? (parseFloat(width) || null) : null,
      depth: shape === 'rectangle' ? (parseFloat(depth) || null) : null,
      radius: shape === 'circle' ? (parseFloat(radius) || null) : null,
      height: parseFloat(height) || obstacle.height,
      elevation: parseFloat(elevation) || 0,
      positionX: parseFloat(posX) || 0,
      positionY: parseFloat(posY) || 0,
      rotation: parseFloat(rotation) || 0,
      color,
      opacity,
    })
  }, [shape, width, depth, radius, height, elevation, posX, posY, rotation, color, opacity, pushToCache, obstacle.height])

  const resetForm = () => {
    const saved = savedRef.current
    // Restore cache to last-saved state (undoes drag visual changes)
    const queries = queryClient.getQueriesData<Obstacle[]>({ queryKey: obstacleKeys.lists() })
    for (const [key] of queries) {
      queryClient.setQueryData<Obstacle[]>(key, (old) =>
        old?.map((o) => (o.id === saved.id ? saved : o))
      )
    }
    setName(saved.name)
    setObstacleType(saved.obstacleType)
    setMobility(saved.mobility)
    setShape(saved.shape)
    setWidth(saved.width?.toString() ?? '')
    setDepth(saved.depth?.toString() ?? '')
    setRadius(saved.radius?.toString() ?? '')
    setHeight(saved.height.toString())
    setElevation(saved.elevation.toString())
    setPosX(saved.positionX.toString())
    setPosY(saved.positionY.toString())
    setRotation(saved.rotation.toString())
    setColor(saved.color)
    setOpacity(saved.opacity)
    setLabelVisible(saved.labelVisible)
    setParticipatesInCollision(saved.participatesInCollision)
    setClearanceBuffer(saved.clearanceBufferFt.toString())
    setIsActive(saved.isActive)
    setNotes(saved.notes ?? '')
  }

  // Dirty tracking — compare form against last-saved server state (not cache)
  const saved = savedRef.current
  const isDirty =
    name !== saved.name ||
    obstacleType !== saved.obstacleType ||
    mobility !== saved.mobility ||
    shape !== saved.shape ||
    width !== (saved.width?.toString() ?? '') ||
    depth !== (saved.depth?.toString() ?? '') ||
    radius !== (saved.radius?.toString() ?? '') ||
    height !== saved.height.toString() ||
    elevation !== saved.elevation.toString() ||
    posX !== saved.positionX.toString() ||
    posY !== saved.positionY.toString() ||
    rotation !== saved.rotation.toString() ||
    color !== saved.color ||
    opacity !== saved.opacity ||
    labelVisible !== saved.labelVisible ||
    participatesInCollision !== saved.participatesInCollision ||
    clearanceBuffer !== saved.clearanceBufferFt.toString() ||
    isActive !== saved.isActive ||
    (notes.trim() || null) !== (saved.notes ?? null)

  const handleSave = () => {
    const data: UpdateObstacleInput = {
      name,
      obstacleType,
      mobility,
      shape,
      ...(shape === 'rectangle'
        ? { width: parseFloat(width) || null, depth: parseFloat(depth) || null, radius: null }
        : { radius: parseFloat(radius) || null, width: null, depth: null }),
      height: parseFloat(height) || obstacle.height,
      elevation: parseFloat(elevation) || 0,
      positionX: parseFloat(posX) || 0,
      positionY: parseFloat(posY) || 0,
      rotation: parseFloat(rotation) || 0,
      color,
      opacity,
      labelVisible,
      participatesInCollision,
      clearanceBufferFt: parseFloat(clearanceBuffer) || 0,
      isActive,
      notes: notes.trim() || null,
    }
    update.mutate({ id: obstacle.id, data }, {
      onSuccess: () => {
        // Update saved snapshot so dirty tracking resets
        savedRef.current = { ...savedRef.current, ...data } as Obstacle
      },
    })
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden" onWheel={(e) => e.stopPropagation()}>
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onBack}>
          {closeIcon === 'close' ? <X className="h-4 w-4" /> : <ArrowLeft className="h-4 w-4" />}
        </Button>
        <span className="text-sm font-medium truncate">{obstacle.name}</span>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-5">
          {/* Identity */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Identity</h4>

            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-8 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Type</Label>
                <Select
                  value={obstacleType}
                  onValueChange={(v) => setObstacleType(v as ObstacleType)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OBSTACLE_TYPES.map((t) => (
                      <SelectItem key={t} value={t} className="text-xs">
                        {OBSTACLE_TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Mobility</Label>
                <Select
                  value={mobility}
                  onValueChange={(v) => setMobility(v as ObstacleMobility)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MOBILITIES.map((m) => (
                      <SelectItem key={m} value={m} className="text-xs">
                        {OBSTACLE_MOBILITY_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Shape</Label>
              <Select
                value={shape}
                onValueChange={(v) => setShape(v as ObstacleShape)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SHAPES.map((s) => (
                    <SelectItem key={s} value={s} className="text-xs capitalize">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          <Separator />

          {/* Dimensions */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Dimensions (ft)</h4>

            {shape === 'rectangle' ? (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Width</Label>
                  <Input
                    type="number"
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    className="h-8 text-sm"
                    min={0}
                    step={0.5}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Depth</Label>
                  <Input
                    type="number"
                    value={depth}
                    onChange={(e) => setDepth(e.target.value)}
                    className="h-8 text-sm"
                    min={0}
                    step={0.5}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">Radius</Label>
                <Input
                  type="number"
                  value={radius}
                  onChange={(e) => setRadius(e.target.value)}
                  className="h-8 text-sm"
                  min={0}
                  step={0.25}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Height</Label>
                <Input
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  className="h-8 text-sm"
                  min={0.1}
                  step={0.5}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Elevation</Label>
                <Input
                  type="number"
                  value={elevation}
                  onChange={(e) => setElevation(e.target.value)}
                  className="h-8 text-sm"
                  min={0}
                  step={0.5}
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* Position */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Position in Unit (ft)</h4>
            <p className="text-[10px] text-muted-foreground">Relative to unit origin</p>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">X</Label>
                <Input
                  type="number"
                  value={posX}
                  onChange={(e) => setPosX(e.target.value)}
                  className="h-8 text-sm"
                  step={0.5}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Y</Label>
                <Input
                  type="number"
                  value={posY}
                  onChange={(e) => setPosY(e.target.value)}
                  className="h-8 text-sm"
                  step={0.5}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Rotation</Label>
                <Input
                  type="number"
                  value={rotation}
                  onChange={(e) => setRotation(e.target.value)}
                  className="h-8 text-sm"
                  step={15}
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* Schedule */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Schedule</h4>

            <div className="flex items-center justify-between">
              <Label className="text-xs">Active</Label>
              <Switch
                checked={isActive}
                onCheckedChange={(v) => setIsActive(v)}
              />
            </div>
          </section>

          <Separator />

          {/* Collision */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Collision</h4>

            <div className="flex items-center justify-between">
              <Label className="text-xs">Participates in collision</Label>
              <Switch
                checked={participatesInCollision}
                onCheckedChange={(v) => setParticipatesInCollision(v)}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Clearance buffer (ft)</Label>
              <Input
                type="number"
                value={clearanceBuffer}
                onChange={(e) => setClearanceBuffer(e.target.value)}
                className="h-8 text-sm"
                min={0}
                step={0.5}
              />
            </div>
          </section>

          <Separator />

          {/* Appearance */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Appearance</h4>

            <div className="flex items-center gap-3">
              <Label className="text-xs">Color</Label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-8 h-8 rounded border cursor-pointer"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Opacity ({Math.round(opacity * 100)}%)</Label>
              <Slider
                value={[opacity]}
                min={0}
                max={1}
                step={0.05}
                onValueChange={([v]) => setOpacity(v)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-xs">Show label</Label>
              <Switch
                checked={labelVisible}
                onCheckedChange={(v) => setLabelVisible(v)}
              />
            </div>
          </section>

          <Separator />

          {/* Notes */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Notes</h4>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              className="text-sm min-h-[60px]"
            />
          </section>

          <Separator />

          {/* Actions — icon buttons + Save */}
          <section className="flex items-center gap-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={!isDirty}
                    onClick={resetForm}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p>Reset</p></TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => duplicate.mutate(obstacle)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p>Duplicate</p></TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => {
                      remove.mutate(obstacle.id)
                      onBack()
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p>Delete</p></TooltipContent>
              </Tooltip>

            <div className="flex-1" />

            <Button
              size="sm"
              disabled={!isDirty || update.isPending}
              onClick={handleSave}
            >
              <Save className="h-3.5 w-3.5 mr-1.5" />
              {update.isPending ? 'Saving...' : 'Save'}
            </Button>
          </section>
        </div>
      </ScrollArea>
    </div>
  )
}
